/**
 * Enhanced Image Cache System for Profile Pictures
 * Provides intelligent preloading, caching, and cache invalidation
 */

import React from 'react';

interface CachedImage {
  url: string;
  blob: Blob;
  cachedAt: number;
  lastAccessed: number;
}

interface ImageCacheStats {
  cacheHits: number;
  cacheMisses: number;
  cacheSize: number;
  totalImages: number;
}

class ProfileImageCache {
  private cache = new Map<string, CachedImage>();
  private preloadQueue = new Set<string>();
  private readonly maxCacheSize = 50; // Maximum number of images to cache
  private readonly cacheExpiry = 60 * 60 * 1000; // 1 hour in milliseconds
  private readonly maxFileSize = 5 * 1024 * 1024; // 5MB max file size
  private stats: ImageCacheStats = {
    cacheHits: 0,
    cacheMisses: 0,
    cacheSize: 0,
    totalImages: 0
  };

  /**
   * Get cached image URL or load and cache if not available
   */
  async getImageUrl(originalUrl: string): Promise<string> {
    if (!originalUrl) return originalUrl;

    const cachedImage = this.cache.get(originalUrl);
    
    // Check if we have a valid cached version
    if (cachedImage && this.isCacheValid(cachedImage)) {
      cachedImage.lastAccessed = Date.now();
      this.stats.cacheHits++;
      
      // Return blob URL for cached image
      return URL.createObjectURL(cachedImage.blob);
    }

    // Cache miss - load and cache the image
    this.stats.cacheMisses++;
    
    try {
      const blob = await this.fetchAndCacheImage(originalUrl);
      return URL.createObjectURL(blob);
    } catch (error) {
      console.warn('Failed to cache image, using original URL:', error);
      return originalUrl; // Fallback to original URL
    }
  }

  /**
   * Preload images in the background for better UX
   */
  async preloadImages(urls: string[]): Promise<void> {
    const uncachedUrls = urls.filter(url => 
      url && !this.cache.has(url) && !this.preloadQueue.has(url)
    );

    if (uncachedUrls.length === 0) return;

    // Add to preload queue to prevent duplicate requests
    uncachedUrls.forEach(url => this.preloadQueue.add(url));

    // Preload in batches to avoid overwhelming the browser
    const batchSize = 3;
    for (let i = 0; i < uncachedUrls.length; i += batchSize) {
      const batch = uncachedUrls.slice(i, i + batchSize);
      
      await Promise.allSettled(
        batch.map(async (url) => {
          try {
            await this.fetchAndCacheImage(url);
          } catch (error) {
            console.debug('Preload failed for:', url);
          } finally {
            this.preloadQueue.delete(url);
          }
        })
      );

      // Small delay between batches to prevent browser overload
      if (i + batchSize < uncachedUrls.length) {
        await new Promise(resolve => setTimeout(resolve, 100));
      }
    }
  }

  /**
   * Invalidate cache for a specific URL (useful when profile is updated)
   */
  invalidateImage(url: string): void {
    const cachedImage = this.cache.get(url);
    if (cachedImage) {
      // Revoke the blob URL to free memory
      URL.revokeObjectURL(URL.createObjectURL(cachedImage.blob));
      this.cache.delete(url);
      this.updateStats();
    }
  }

  /**
   * Clear expired cache entries and enforce size limits
   */
  cleanup(): void {
    const now = Date.now();
    const expiredUrls: string[] = [];

    // Find expired entries
    this.cache.forEach((cachedImage, url) => {
      if (!this.isCacheValid(cachedImage)) {
        expiredUrls.push(url);
      }
    });

    // Remove expired entries
    expiredUrls.forEach(url => {
      const cachedImage = this.cache.get(url);
      if (cachedImage) {
        URL.revokeObjectURL(URL.createObjectURL(cachedImage.blob));
        this.cache.delete(url);
      }
    });

    // Enforce cache size limit by removing least recently accessed items
    if (this.cache.size > this.maxCacheSize) {
      const cacheEntries: Array<[string, CachedImage]> = [];
      this.cache.forEach((value, key) => {
        cacheEntries.push([key, value]);
      });
      
      const sortedEntries = cacheEntries
        .sort((a, b) => a[1].lastAccessed - b[1].lastAccessed);

      const toRemove = sortedEntries.slice(0, this.cache.size - this.maxCacheSize);
      toRemove.forEach(([url, cachedImage]) => {
        URL.revokeObjectURL(URL.createObjectURL(cachedImage.blob));
        this.cache.delete(url);
      });
    }

    this.updateStats();
  }

  /**
   * Get cache statistics for debugging
   */
  getCacheStats(): ImageCacheStats {
    return { ...this.stats };
  }

  /**
   * Clear entire cache
   */
  clearCache(): void {
    // Revoke all blob URLs to prevent memory leaks
    this.cache.forEach((cachedImage) => {
      URL.revokeObjectURL(URL.createObjectURL(cachedImage.blob));
    });
    
    this.cache.clear();
    this.preloadQueue.clear();
    this.updateStats();
  }

  private async fetchAndCacheImage(url: string): Promise<Blob> {
    try {
      const response = await fetch(url, {
        cache: 'force-cache', // Use browser cache when possible
        mode: 'cors'
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch image: ${response.status}`);
      }

      const blob = await response.blob();

      // Check file size limit
      if (blob.size > this.maxFileSize) {
        console.warn(`Image too large (${blob.size} bytes), skipping cache:`, url);
        return blob; // Return blob but don't cache it
      }

      // Cache the image
      const cachedImage: CachedImage = {
        url,
        blob,
        cachedAt: Date.now(),
        lastAccessed: Date.now()
      };

      this.cache.set(url, cachedImage);
      this.updateStats();

      // Clean up if cache is getting too large
      if (this.cache.size > this.maxCacheSize * 1.2) {
        this.cleanup();
      }

      return blob;
    } catch (error) {
      console.error('Error fetching and caching image:', error);
      throw error;
    }
  }

  private isCacheValid(cachedImage: CachedImage): boolean {
    const now = Date.now();
    return (now - cachedImage.cachedAt) < this.cacheExpiry;
  }

  private updateStats(): void {
    this.stats.cacheSize = this.cache.size;
    this.stats.totalImages = this.cache.size;
  }
}

// Create singleton instance
export const profileImageCache = new ProfileImageCache();

// Cleanup cache periodically (every 5 minutes)
setInterval(() => {
  profileImageCache.cleanup();
}, 5 * 60 * 1000);

// Enhanced hook for cached profile images
export const useCachedProfileImage = (imageUrl: string | null | undefined) => {
  const [cachedUrl, setCachedUrl] = React.useState<string | null>(null);
  const [loading, setLoading] = React.useState(false);

  React.useEffect(() => {
    if (!imageUrl) {
      setCachedUrl(null);
      return;
    }

    let isCurrent = true;
    setLoading(true);

    profileImageCache.getImageUrl(imageUrl)
      .then(url => {
        if (isCurrent) {
          setCachedUrl(url);
          setLoading(false);
        }
      })
      .catch(error => {
        if (isCurrent) {
          console.warn('Failed to load cached image:', error);
          setCachedUrl(imageUrl); // Fallback to original URL
          setLoading(false);
        }
      });

    return () => {
      isCurrent = false;
    };
  }, [imageUrl]);

  return { cachedUrl, loading };
};